import { useEffect, useState } from 'react';
import { HOME_PATH } from '@/lib/paths';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useUserRole } from '@/hooks/useUserRole';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, Users, Mail, CreditCard, BarChart3 } from 'lucide-react';
import WaitlistManagement from '@/components/admin/WaitlistManagement';
import UserManagement from '@/components/admin/UserManagement';
import SubscriptionManagement from '@/components/admin/SubscriptionManagement';
import AnalyticsDashboard from '@/components/admin/AnalyticsDashboard';
import { toast } from 'sonner';

const Admin = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const { isAdmin, roles, isLoading: roleLoading } = useUserRole(user);
  const [stats, setStats] = useState<any>(null);
  const [isLoadingStats, setIsLoadingStats] = useState(true);

  useEffect(() => {
    const getUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setUser(session?.user ?? null);
    };
    getUser();
  }, []);

  useEffect(() => {
    // Only redirect if roles are loaded AND user is definitively not an admin
    if (!roleLoading && user && roles.length > 0 && !isAdmin) {
      toast.error('Access denied: Admin privileges required');
      navigate(HOME_PATH);
    }
  }, [isAdmin, roleLoading, user, roles, navigate]);

  useEffect(() => {
    const fetchStats = async () => {
      if (!user || !isAdmin) return;
      
      try {
        // Fetch stats from multiple tables
        const [waitlistRes, rolesRes] = await Promise.all([
          supabase.from('waitlist_signups').select('created_at', { count: 'exact' }),
          supabase.from('user_roles').select('role', { count: 'exact' })
        ]);

        if (waitlistRes.error) throw waitlistRes.error;
        if (rolesRes.error) throw rolesRes.error;

        const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
        const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);

        const recent7Days = waitlistRes.data?.filter(
          s => new Date(s.created_at) > sevenDaysAgo
        ).length || 0;

        const recent30Days = waitlistRes.data?.filter(
          s => new Date(s.created_at) > thirtyDaysAgo
        ).length || 0;

        const adminCount = rolesRes.data?.filter(r => r.role === 'admin').length || 0;
        const premiumCount = rolesRes.data?.filter(r => r.role === 'premium').length || 0;
        const freeCount = rolesRes.data?.filter(r => r.role === 'free').length || 0;

        setStats({
          total_waitlist_signups: waitlistRes.count || 0,
          recent_signups_7days: recent7Days,
          recent_signups_30days: recent30Days,
          total_users: rolesRes.count || 0,
          admin_count: adminCount,
          premium_count: premiumCount,
          free_count: freeCount,
          total_profiles: rolesRes.count || 0
        });
      } catch (error) {
        console.error('Error fetching admin stats:', error);
        toast.error('Failed to load dashboard statistics');
      } finally {
        setIsLoadingStats(false);
      }
    };

    if (isAdmin && user) {
      fetchStats();
    }
  }, [user, isAdmin]);

  if (roleLoading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <div>
          <h1 className="text-4xl font-bold text-foreground">Admin Dashboard</h1>
          <p className="text-muted-foreground mt-2">Manage your platform from one central location</p>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Waitlist Signups</CardTitle>
              <Mail className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {isLoadingStats ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <div className="text-2xl font-bold">{stats?.total_waitlist_signups || 0}</div>
                  <p className="text-xs text-muted-foreground">
                    {stats?.recent_signups_7days || 0} in last 7 days
                  </p>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Users</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {isLoadingStats ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <div className="text-2xl font-bold">{stats?.total_users || 0}</div>
                  <p className="text-xs text-muted-foreground">
                    {stats?.admin_count || 0} admins, {stats?.premium_count || 0} premium
                  </p>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Profiles</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {isLoadingStats ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <div className="text-2xl font-bold">{stats?.total_profiles || 0}</div>
                  <p className="text-xs text-muted-foreground">
                    {stats?.free_count || 0} free tier
                  </p>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Recent Activity</CardTitle>
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {isLoadingStats ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <div className="text-2xl font-bold">{stats?.recent_signups_30days || 0}</div>
                  <p className="text-xs text-muted-foreground">signups in 30 days</p>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Main Tabs */}
        <Tabs defaultValue="waitlist" className="space-y-4">
          <TabsList className="flex w-full overflow-x-auto scrollbar-hide gap-1 p-1 h-auto justify-start sm:justify-center">
            <TabsTrigger value="waitlist">Waitlist</TabsTrigger>
            <TabsTrigger value="users">Users</TabsTrigger>
            <TabsTrigger value="subscriptions">Subscriptions</TabsTrigger>
            <TabsTrigger value="analytics">Analytics</TabsTrigger>
          </TabsList>

          <TabsContent value="waitlist" className="space-y-4">
            <WaitlistManagement />
          </TabsContent>

          <TabsContent value="users" className="space-y-4">
            <UserManagement />
          </TabsContent>

          <TabsContent value="subscriptions" className="space-y-4">
            <SubscriptionManagement />
          </TabsContent>

          <TabsContent value="analytics" className="space-y-4">
            <AnalyticsDashboard />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Admin;
